import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useTrafficApi } from '@store/api/trafficApi';
import type { TrafficStats } from '@store/api/interfaces';

/**
 * Статистика основного трафика: ответ `GET /traffic` для строки на главной.
 *
 * Загрузка best-effort, как у резерва: строка — справка, а не функция, и её
 * недоступность (старый бэкенд без эндпоинта, панель не ответила) не должна
 * ломать главный экран — строка просто не показывается.
 */
export const useTrafficStore = defineStore('traffic', () => {
	const trafficApi = useTrafficApi();

	const stats = ref<TrafficStats | null>(null);

	// Один запрос на несколько одновременных вызовов load().
	let pending: Promise<void> | null = null;

	// Строка на главной — про основной ключ; резерв показывает своя карточка
	// из GET /reserve, блок `whitelist` здесь не используется
	const periodUsedBytes = computed<number | null>(() => stats.value?.main?.period_used_bytes ?? null);
	const periodLimitBytes = computed<number | null>(() => stats.value?.main?.period_limit_bytes ?? null);
	const lifetimeUsedBytes = computed<number | null>(() => stats.value?.main?.lifetime_used_bytes ?? null);

	// Показывать есть что, если известна хотя бы одна из двух цифр
	const hasData = computed<boolean>(() => periodUsedBytes.value !== null || lifetimeUsedBytes.value !== null);

	// Без кэша: цифры привязаны к пользователю и растут с каждым подключением
	const load = (): Promise<void> => {
		if (pending) return pending;

		pending = trafficApi.http.getStats()
			.then((data) => {
				stats.value = data;
			})
			.catch((error) => {
				console.info('traffic stats unavailable', error);
				stats.value = null;
			})
			.finally(() => {
				pending = null;
			});

		return pending;
	};

	return {
		stats,
		periodUsedBytes,
		periodLimitBytes,
		lifetimeUsedBytes,
		hasData,
		load
	};
});
